import apiClient from './client';
import type { ApiResponse } from '@/types';

export interface AnalyticsTenantRank {
  tenantId: string;
  tenantName: string;
  views: number;
  users: number;
}

export interface AnalyticsPageStat {
  pagePath: string;
  views: number;
  completed: number;
  avgDurationMs: number;
  totalDurationMs: number;
  users: number;
}

export interface AnalyticsDailyPoint {
  day: string;
  views: number;
}

export interface AnalyticsTotals {
  views: number;
  users: number;
  avgDurationMs: number;
  totalDurationMs: number;
}

export interface AnalyticsSummary {
  isPlatformAdmin: boolean;
  range: { from: string; to: string };
  totals: AnalyticsTotals;
  pages: AnalyticsPageStat[];
  daily: AnalyticsDailyPoint[];
  tenants: AnalyticsTenantRank[];
}

/** 我的下线使用概况（推荐人视角，仅汇总指标） */
export interface DownlineUsageItem {
  userId: string;
  displayName: string;
  /** 已脱敏邮箱 */
  email: string;
  companyName: string | null;
  /** 近 30 天访问次数 */
  views: number;
  /** 近 30 天活跃天数（去重日期） */
  activeDays: number;
  /** 近 30 天累计时长 ms */
  totalDurationMs: number;
  lastActiveAt: string | null;
  /** 常用模块 Top3 */
  topModules: { path: string; name: string; views: number }[];
  /** 最近一次利润自测徽标（汇总级，不含逐题答案）；从未测过为 null */
  quiz: {
    totalScore: number;
    redCount: number;
    weakest: { name: string; score: number; max: number; ratio: number } | null;
    createdAt: string;
  } | null;
  /** 深度使用 / 观望中 / 未激活 / 流失风险 */
  status: string;
  /** 规则化跟进建议 */
  suggestion: string;
}

export interface DownlineUsageSummary {
  windowDays: number;
  generatedAt?: string;
  list: DownlineUsageItem[];
}

/** 前端埋点上报载荷（enter/leave/ping） */
export interface AnalyticsTrackPayload {
  type: 'enter' | 'leave' | 'ping';
  page_path: string;
  session_id: string;
  /** leave 时携带，单位 ms */
  duration_ms?: number;
  /** sendBeacon 兜底通道：无法设自定义请求头，把 token 放 body（后端按 body 通道解析） */
  token?: string;
}

const unwrap = async <T>(p: Promise<{ data: ApiResponse<T> }>): Promise<T> => {
  const resp = await p;
  return resp.data.data as T;
};

export const analyticsApi = {
  /**
   * 用户使用分析汇总（仅平台管理员）。
   * @param params.from  ISO 时间字符串（默认近 30 天）
   * @param params.to    ISO 时间字符串（默认现在）
   * @param params.tenantId 可选，按租户过滤
   */
  getSummary: (params?: { from?: string; to?: string; tenantId?: string }) =>
    unwrap<AnalyticsSummary>(apiClient.get('/analytics/summary', { params })),

  /** 我的下线使用概况（任何登录用户，仅自己直接下线的近 30 天汇总） */
  getDownlineSummary: () =>
    unwrap<DownlineUsageSummary>(apiClient.get('/analytics/downline-summary')),

  /**
   * 前端埋点上报（enter/leave/ping）。静默失败，不影响主流程。
   * 走 apiClient 自动带 Authorization，后端按 header 通道解析身份（验签失败降级 decode）。
   */
  track: (payload: AnalyticsTrackPayload) =>
    apiClient.post('/analytics/track', payload).catch(() => undefined),
};

export default analyticsApi;
